import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { useOverviewVariables } from './useOverviewVariables';

export interface OverviewSessionMetricsData {
  avg_ai_csat: number | null;
  avg_ai_csat_insights: number | null;
  ai_csat_insights_count: number | null;
  avg_csat_user_rating: number | null;
  csat_user_rating_count: number | null;
  containment_rate: number | null;
  total_conversations: number | null;
  meaningful_conversations: number | null;
  nonmeaningful_conversations: number | null;
  meaningful_brain_conversations: number | null;
  meaningful_collection_conversations: number | null;
  median_ai_session_length_seconds: number | null;
  median_human_session_length_seconds: number | null;
}

interface QueryResponse {
  rows: OverviewSessionMetricsData[];
}

export const ACCOUNT_CONVERSATIONS_SUMMARY = gql`
  query AccountConversationsSummary(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $channels: _text
    $deskIds: _uuid
    $integrationIds: _uuid
    $isTest: Boolean
    $minNumUserMessages: Int
    $minAvgCsatScore: Float
    $maxAvgCsatScore: Float
  ) {
    rows: account_conversations_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        channels: $channels
        desk_ids: $deskIds
        integration_ids: $integrationIds
        is_test: $isTest
        min_num_user_messages: $minNumUserMessages
        min_avg_csat_score: $minAvgCsatScore
        max_avg_csat_score: $maxAvgCsatScore
      }
    ) {
      avg_ai_csat
      avg_ai_csat_insights
      ai_csat_insights_count
      avg_csat_user_rating
      csat_user_rating_count
      containment_rate
      total_conversations
      meaningful_conversations
      nonmeaningful_conversations
      meaningful_brain_conversations
      meaningful_collection_conversations
      median_ai_session_length_seconds
      median_human_session_length_seconds
    }
  }
`;

export const useOverviewSessionMetrics = () => {
  const { variables, skip } = useOverviewVariables();

  const { loading, data, error, refetch } = useQuery<QueryResponse>(ACCOUNT_CONVERSATIONS_SUMMARY, {
    variables,
    skip,
  });

  const row = data?.rows?.[0] ?? null;

  const onRefetch = useCallback(() => {
    refetch(variables);
  }, [refetch, variables]);

  return { isLoading: loading, error, data: row, onRefetch };
};
